import { useQueries, type UseQueryOptions } from '@tanstack/react-query';
import { useRef } from 'react';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { fetchUsersBulk } from '~/data/user';

import { vestigasApi } from '~/services/kyClient';

import { DeliveryNoteActionObject } from '~/models/deliveries';
import { useInitDlns } from '~/models/deliveries/DeliveryNote/utils/hooks';
import { ValueGroupObject } from '~/models/deliveries/valueGroup';

import { unique } from '@vestigas/utils';
import { parseDate } from '~/utils/dateUtils';
import { promiseAllThrottled } from '~/utils/promise';

import { queryKeysDeliveryNote } from './queryKeys';
import {
  type DeliveryNoteListItem,
  type DeliveryNoteSearchParams,
} from './types';
import { zodDeliveryNotesPayloadSchema } from './useQueryDeliveryNotes';
import { fetchDeliveryNoteSearchCount } from './useQueryDeliveryNoteSearchCount';

type DeliveryNotesResponse = {
  assets: DeliveryNoteListItem[];
  hasNextPage: boolean;
  paginatedCount: number;
  sequenceNumber: string;
  totalCount: number;
};

/**
 * Fetches all delivery notes based on search parameters from the API.
 *
 * @param searchParams - The search parameters for filtering delivery notes
 * @returns The matching delivery notes data
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/search_delivery_note_asset_delivery_note_search_post
 */
const fetchDeliveryNotes = async (searchParams: DeliveryNoteSearchParams) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.GET_ALL(), {
      json: searchParams,
      validate: {
        body: zodDeliveryNotesPayloadSchema,
      },
    })
    .json<DeliveryNotesResponse>();

  return response;
};

/**
 * React Query based custom hook for getting the data for all delivery notes with given search parameters.
 * Handles pagination.
 *
 * @param searchParams - The search parameters for filtering delivery notes
 * @param options - Additional options for the useQuery hook
 * @returns The result of the useQuery hook containing the count
 */
export const useQueryDeliveryNotesChanges = (
  searchParams: DeliveryNoteSearchParams,
  options?: Omit<UseQueryOptions<number>, 'queryKey' | 'queryFn'>,
) => {
  const initDlns = useInitDlns();

  const previousDataRef = useRef<
    | {
        deliveryNotes: Awaited<ReturnType<typeof fetchDeliveryNotes>> & {
          rows: any[];
        };
        count: number | undefined;
      }
    | undefined
  >(undefined);

  const countQueryParams = {
    filterConfig: searchParams?.filterConfig,
  };

  const queries = useQueries({
    queries: [
      {
        enabled: options?.enabled !== false,
        placeholderData: () => previousDataRef.current?.deliveryNotes,
        async queryFn() {
          const response = await fetchDeliveryNotes(searchParams);

          const initializedAssets = response.assets?.length
            ? await initDlns(response.assets)
            : [];

          // Fetch the details of all users who made changes in one bulk request
          const userIds = unique(
            response.assets?.flatMap((asset) =>
              asset.assetChainDocs?.map((chain) =>
                chain?.createdBy?.split('_')?.pop(),
              ),
            ),
          ).filter(Boolean) as UUID[];
          const users =
            userIds.length > 0 ? await fetchUsersBulk(userIds, true) : [];

          const rows = initializedAssets.map((deliveryNote) => {
            const chains = response.assets.find(
              ({ id }) => id === deliveryNote.id,
            )?.assetChainDocs;

            deliveryNote.changes = [];

            for (const [index, chain] of chains.entries()) {
              const deliveryNoteAction = DeliveryNoteActionObject.create(
                chain,
                deliveryNote,
              );

              const creatingUserId = chain?.createdBy?.split('_')?.pop();
              const user = users.find(({ id }) => id === creatingUserId);
              const companyName = user?.companyName;

              // Initialize history with company name
              deliveryNote.addChainToHistory(chain, companyName, index === 0);

              // Update deliveryNote changes by reference
              deliveryNoteAction.setChanges(deliveryNote);
            }

            const { getCurrentValue } = ValueGroupObject;
            const {
              acceptState,
              changes,
              combinedState,
              id,
              modifiedOn,
              number,
              processState,
            } = deliveryNote;

            if (changes.length === 0) {
              return null;
            }

            const status = [processState, acceptState, combinedState]
              .map((value) => getCurrentValue(value))
              .join(';');
            const changedFields = [
              ...new Set(changes.map(({ name }) => name)),
            ].join(', ');
            const changedBy = [
              ...new Set(
                changes.flatMap(
                  ({ history }) => history?.map(({ company }) => company) ?? [],
                ),
              ),
            ]
              .filter(Boolean)
              .join(', ');

            return {
              changedBy,
              changedDate: parseDate(getCurrentValue(modifiedOn)),
              changedFields,
              id: getCurrentValue(id),
              number: getCurrentValue(number),
              status,
            };
          });

          const result = {
            ...response,
            rows: rows.filter(Boolean),
          };

          previousDataRef.current = {
            ...previousDataRef.current,
            deliveryNotes: result,
          };

          return result;
        },
        queryKey: queryKeysDeliveryNote.getAll(searchParams),
      },
      {
        enabled: options?.enabled !== false,
        placeholderData: () => previousDataRef.current?.count,
        async queryFn({ signal }: { signal: AbortSignal }) {
          const count = await fetchDeliveryNoteSearchCount(countQueryParams, {
            signal,
          });

          previousDataRef.current = {
            ...previousDataRef.current,
            count,
          };

          return count;
        },
        queryKey: queryKeysDeliveryNote.getSearchCount(countQueryParams),
      },
    ],
  });

  const [deliveryNotesQuery, deliveryNotesCountQuery] = queries;

  const isLoading = queries.some(({ isLoading }) => isLoading);
  const isFetching = queries.some(({ isFetching }) => isFetching);
  const isError = queries.some(({ isError }) => isError);
  const isSuccess = queries.every(({ isSuccess }) => isSuccess);

  const data = {
    hasNextPage: deliveryNotesQuery.data?.hasNextPage,
    paginatedCount: deliveryNotesQuery.data?.paginatedCount,
    rows: deliveryNotesQuery.data?.rows ?? [],
    sequenceNumber: deliveryNotesQuery.data?.sequenceNumber,
    totalCount: deliveryNotesCountQuery.data,
  };

  const refetch = async () => {
    await promiseAllThrottled(queries.map(async (query) => query.refetch()));
  };

  return {
    data,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  };
};
